import React, { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import Button from '../../components/ui/Button';
import SearchInput from '../../components/ui/SearchInput';
import {
  getSupplierBalances,
  type SupplierBalanceRow,
  type SupplierBalanceSort,
  type SupplierBalancesReport,
} from '../../lib/accountsPayableReportsApi';
import { downloadObjectsCsv } from '../../utils/downloadCsv';
import { balanceFigureHref, type BalanceFigure } from './drillDown';
import { billCountLabel, formatReportMoney, supplierLabel } from './format';
import { IndicativeLine, ReportFrame } from './ReportFrame';
import ReportLink from './ReportLink';
import { useReport } from './useReport';

/**
 * What we owe each supplier (D49 phase 2), one row per supplier per currency.
 *
 *   Owed        every open bill, on hold or disputed included
 *   Overdue     past its due date, leaving out bills on hold or disputed (due today is not late)
 *   On hold     the held part of Owed
 *   Open credit the supplier's unspent vendor credit (D44), matched to the bills BY NAME
 *   Net owed    Owed minus Open credit, the true figure, negative when the credit is larger
 */

const FIELD_INPUT =
  'h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20';

const SORTS: ReadonlyArray<{ value: SupplierBalanceSort; label: string }> = [
  { value: 'owed', label: 'Most owed' },
  { value: 'netOwed', label: 'Most owed after credit' },
  { value: 'overdue', label: 'Most overdue' },
  { value: 'oldest', label: 'Oldest overdue' },
  { value: 'name', label: 'Name A to Z' },
];

function daysLabel(days: number | null): string {
  if (days === null) return '--';
  return days === 1 ? '1 day' : `${days} days`;
}

/** The rows of the CSV, one per supplier per currency. */
export function supplierBalancesCsvRows(report: SupplierBalancesReport): Array<Record<string, unknown>> {
  const rows: Array<Record<string, unknown>> = [];
  for (const block of report.currencies) {
    for (const row of block.suppliers) {
      rows.push({
        Currency: block.currency,
        Supplier: supplierLabel(row.supplierName),
        Owed: row.owed,
        Overdue: row.overdue,
        'Oldest overdue (days)': row.oldestOverdueDays ?? '',
        'On hold or disputed': row.onHoldDisputed,
        'Open credit': row.openCredit,
        'Net owed': row.netOwed,
        'Open bills': row.openBillCount,
      });
    }
    rows.push({
      Currency: block.currency,
      Supplier: 'All suppliers',
      Owed: block.totals.owed,
      Overdue: block.totals.overdue,
      'Oldest overdue (days)': '',
      'On hold or disputed': block.totals.onHoldDisputed,
      'Open credit': block.totals.openCredit,
      'Net owed': block.totals.netOwed,
      'Open bills': block.totals.openBillCount,
    });
  }
  return rows;
}

function Figure({
  report,
  currency,
  supplierName,
  who,
  figure,
  amount,
}: {
  report: SupplierBalancesReport;
  currency: string;
  supplierName: string | null;
  who: string;
  figure: BalanceFigure;
  amount: number;
}) {
  const heading = { owed: 'owed', overdue: 'overdue', onHoldDisputed: 'on hold or disputed' }[figure];
  if (!amount) {
    return (
      <span className="text-slate-400">
        <span aria-hidden="true">--</span>
        <span className="sr-only">nothing</span>
      </span>
    );
  }
  const money = formatReportMoney(amount, currency);
  return (
    <ReportLink
      href={balanceFigureHref(report, { currency, supplierName }, figure)}
      label={`${money} ${heading}, ${who}. Open these bills`}
    >
      {money}
    </ReportLink>
  );
}

export const SupplierBalancesView: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [sort, setSort] = useState<SupplierBalanceSort>('owed');

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search.trim()), 250);
    return () => window.clearTimeout(t);
  }, [search]);

  const state = useReport(async () => {
    const res = await getSupplierBalances({ q: debounced, sort });
    return res.data;
  }, [debounced, sort]);

  const report = state.data;
  const csvRows = useMemo(() => (report ? supplierBalancesCsvRows(report) : []), [report]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="w-64 max-w-full">
          <span className="mb-1 block text-xs font-medium text-slate-600">Find a supplier</span>
          <SearchInput value={search} onChange={setSearch} placeholder="Supplier name" aria-label="Find a supplier" />
        </div>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">Sort by</span>
          <select value={sort} onChange={(e) => setSort(e.target.value as SupplierBalanceSort)} className={FIELD_INPUT}>
            {SORTS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <div className="ml-auto">
          <Button
            variant="primary"
            icon={<Download aria-hidden="true" className="h-4 w-4" />}
            disabled={csvRows.length === 0}
            onClick={() => downloadObjectsCsv('ap-supplier-balances.csv', csvRows)}
          >
            Export CSV
          </Button>
        </div>
      </div>
      <p className="mb-4 max-w-[80ch] text-xs text-slate-600">
        Owed counts every bill that still owes money, including bills on hold or disputed. Overdue leaves those
        out, and a bill due today is not overdue yet. Open credit is a supplier credit not yet spent on a bill,
        matched to the supplier by name, so a supplier billed under two spellings may show its credit on only one.
      </p>

      <ReportFrame
        state={state}
        isEmpty={(data) => data.currencies.length === 0}
        emptyTitle={debounced ? 'No supplier matches that name.' : 'Nothing is owed to suppliers.'}
        emptyDetail={debounced ? 'Check the spelling, or clear the search.' : 'Every bill is paid or credited in full.'}
      >
        {(data) => (
          <>
            {data.currencies.map((block) => (
              <section key={block.currency} aria-label={`Supplier balances in ${block.currency}`} className="mb-6">
                <h3 className="mb-2 text-sm font-semibold text-slate-900">{block.currency}</h3>
                <div
                  role="region"
                  aria-label={`Supplier balances in ${block.currency}, scrolls sideways on a narrow screen`}
                  tabIndex={0}
                  className="relative overflow-x-auto rounded-lg border border-slate-200 bg-white"
                >
                  <table className="data-table w-full min-w-[820px] text-sm">
                    <caption className="sr-only">
                      What is owed to each supplier in {block.currency}: owed, overdue, on hold or disputed, open
                      credit and net owed.
                    </caption>
                    <thead className="border-b border-slate-200 bg-slate-50">
                      <tr>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-slate-600">
                          Supplier
                        </th>
                        {['Owed', 'Overdue', 'Oldest overdue', 'On hold or disputed', 'Open credit', 'Net owed', 'Bills'].map(
                          (heading) => (
                            <th
                              key={heading}
                              scope="col"
                              className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold text-slate-600"
                            >
                              {heading}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {block.suppliers.map((row: SupplierBalanceRow) => (
                        <tr key={row.supplierName} className="border-b border-slate-100">
                          <th scope="row" className="px-3 py-2 text-left text-sm font-medium text-slate-800">
                            {supplierLabel(row.supplierName)}
                          </th>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <Figure report={data} currency={block.currency} supplierName={row.supplierName} who={supplierLabel(row.supplierName)} figure="owed" amount={row.owed} />
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <Figure report={data} currency={block.currency} supplierName={row.supplierName} who={supplierLabel(row.supplierName)} figure="overdue" amount={row.overdue} />
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">{daysLabel(row.oldestOverdueDays)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <Figure report={data} currency={block.currency} supplierName={row.supplierName} who={supplierLabel(row.supplierName)} figure="onHoldDisputed" amount={row.onHoldDisputed} />
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                            {row.openCredit ? formatReportMoney(row.openCredit, block.currency) : '--'}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-900">
                            {formatReportMoney(row.netOwed, block.currency)}
                          </td>
                          <td className="px-3 py-2 text-right text-xs text-slate-600">{billCountLabel(row.openBillCount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                        <th scope="row" className="px-3 py-2 text-left text-sm">
                          All suppliers ({block.totals.supplierCount})
                        </th>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <Figure report={data} currency={block.currency} supplierName={null} who="all suppliers" figure="owed" amount={block.totals.owed} />
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <Figure report={data} currency={block.currency} supplierName={null} who="all suppliers" figure="overdue" amount={block.totals.overdue} />
                        </td>
                        <td className="px-3 py-2" />
                        <td className="px-3 py-2 text-right tabular-nums">
                          <Figure report={data} currency={block.currency} supplierName={null} who="all suppliers" figure="onHoldDisputed" amount={block.totals.onHoldDisputed} />
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {block.totals.openCredit ? formatReportMoney(block.totals.openCredit, block.currency) : '--'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatReportMoney(block.totals.netOwed, block.currency)}</td>
                        <td className="px-3 py-2 text-right text-xs text-slate-600">{billCountLabel(block.totals.openBillCount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </section>
            ))}
            <IndicativeLine
              indicative={data.indicative}
              figureKey="netOwed"
              currencyCount={data.currencies.length}
              what="owed after credit"
            />
          </>
        )}
      </ReportFrame>
    </div>
  );
};

export default SupplierBalancesView;
